import { Popper } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Icons from '@shared/components/core/images/Icons';
import { createUUID } from '@shared/constants/Keys';
import { ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    SplitButtonGroup,
    SplitPrimaryButton,
    SplitSecondaryButton,
    SplitTruncatedLabel,
} from './CommonSplitButtonStyles';

// TODO: This is overkill for simple cases where we just want a button with a dropdown menu, but it provides a lot of flexibility for more complex use cases (custom option labels, controlled vs uncontrolled selected index, etc). We can consider adding a simpler "DropdownButton" component in the future if we find ourselves needing that pattern often.
export interface SplitButtonProps<T = string> {
    options: T[];
    renderOptionLabel?: (option: T) => ReactNode;
    onSelect: (option: T, index: number) => void;
    selectedIndex?: number;
    initialSelectedIndex?: number;
    buttonLabel?: string;
    groupLabel: string;
    onOpen?: () => void;
    className?: string;
}

export default function SplitButton<T = string>({
    options,
    renderOptionLabel = (option) => option as ReactNode,
    onSelect,
    selectedIndex: selectedIndexProp,
    initialSelectedIndex = 0,
    buttonLabel,
    groupLabel,
    onOpen,
    className,
}: SplitButtonProps<T>) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const isControlled = selectedIndexProp !== undefined;
    const [uncontrolledIndex, setUncontrolledIndex] = useState(initialSelectedIndex);
    const selectedIndex = isControlled ? selectedIndexProp! : uncontrolledIndex;
    const anchorRef = useRef<HTMLDivElement>(null);
    const uuid = createUUID();
    const isSingleOption = options.length <= 1;

    const handleMenuItemClick = (_event: React.MouseEvent<HTMLLIElement>, index: number) => {
        if (!isControlled) setUncontrolledIndex(index);
        setOpen(false);
        onSelect(options[index], index);
    };

    const handleToggle = () => {
        setOpen((prev) => {
            const next = !prev;
            if (!prev && onOpen) onOpen();
            return next;
        });
    };

    const handleClose = (event: Event) => {
        if (anchorRef.current?.contains(event.target as HTMLElement)) return;
        setOpen(false);
    };

    return (
        <>
            <SplitButtonGroup
                className={className}
                fullWidth
                variant="contained"
                ref={anchorRef}
                aria-label={groupLabel}>
                <SplitPrimaryButton className="split-button-primary" fullWidth onClick={handleToggle}>
                    <SplitTruncatedLabel>
                        {buttonLabel ?? renderOptionLabel(options[selectedIndex])}
                    </SplitTruncatedLabel>
                </SplitPrimaryButton>
                {!isSingleOption && (
                    <SplitSecondaryButton
                        className="split-button-secondary"
                        size="small"
                        aria-controls={open ? uuid + 'split-button-menu' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="menu"
                        aria-label={t('common:expandMenu')}
                        onClick={handleToggle}>
                        <Icons name="expandArrow" size={14} />
                    </SplitSecondaryButton>
                )}
            </SplitButtonGroup>

            <Popper sx={{ zIndex: 1 }} open={open} anchorEl={anchorRef.current} transition disablePortal>
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList id={uuid + 'split-button-menu'} autoFocusItem>
                                    {options &&
                                        options.map((option, index) => (
                                            <MenuItem
                                                key={index}
                                                selected={index === selectedIndex}
                                                onClick={(event) => handleMenuItemClick(event, index)}>
                                                {renderOptionLabel(option)}
                                            </MenuItem>
                                        ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
}
